/**
 * Per-doc DAG entities(Beta v0.0.1 OCC)
 * -----------------------------------------------------------------------------
 * 對齊 docs/40 §3.6 / §3.7 / §4 / §8 + `api-service/app/projects/dag.py`。
 *
 * **跟既有 types/node.ts 的 AppNode 區隔**:
 * - `AppNode` / `AppEdge`(types/node.ts + types/edge.ts):**reactflow 視覺層**
 *   (`id` / `type` / `position` / `data` 含 kind discriminated union)
 * - `CreativeNode` / `NodeEdge`(本檔):**BE per-doc 儲存層**
 *   (`nid` / `node_kind` / `tool_type` / `config` / `version` OCC 等)
 *
 * Phase 3 canvas rewrite 加 mapper `creativeNodeToAppNode` / `appNodePatchToCreativeNodePatch`。
 *
 * v0.0.1 重大設計:
 * - **per-doc OCC**:每次 PATCH 必帶 `expected_version`;不一致 409 STALE_VERSION
 * - **edges immutable**:無 PATCH endpoint;改 edge 一律 DELETE + POST
 * - **immutable field** 不可進 patch body — 完整清單以 `CREATIVE_NODE_IMMUTABLE_FIELDS`
 *   array 為準(`nid` / `node_kind` / `schema_version` / `version` / `created_at` /
 *   `created_by` / `updated_by` 等;違規回 422 GRAPH_OPS_INVALID)
 */

// ─── Node kind + tool type enum ──────────────────────────────────────────────

export type CreativeNodeKind = 'ROOT' | 'TOOL' | 'OUTPUT';
// @backend-pending(Q2): VIDEO_MERGE config schema 待後端 — `video_merge` tool_type
//   大小寫(此處對齊 dag 小寫慣例)/ 後端是否認得此 tool_type(live persist 目前會
//   422)/ clips array 能否進 typed config(見 VideoMergeNodeConfig)。D5 影片生成鏈
//   合併節點(🟢合併 TOOL → ffmpeg)用。
// R134(docs/75)— `video_short_a/b` 短影片拼裝卡:wire 大小寫 BE 雙向 normalize
// (create/PATCH 收小寫、讀回大寫),此 union 維持 dag 小寫慣例。
// R272(ADR-045 / docs/96)— `video_reel` 動態圖文影片:BE `_normalize_tool_type` 收
// 小寫自動轉大寫(`ToolTypeLiteral` 含 `VIDEO_REEL`),此 union 維持 dag 小寫慣例。
// R350(Omni 1.1 二拍)— `video_omni` 原子綠卡(10 秒單發;「延伸」= 成品藍卡接進
// 下一張卡當參考影片)。wire 大小寫 BE `_normalize_tool_type` 雙向 normalize(同 reel)。
// R399(ADR-052)— `video_seedance` 字節 Seedance 影片卡(flag `seedance_pack`;
// wire 大小寫 BE `_normalize_tool_type` 雙向 normalize,同 reel / omni)。
export type CreativeNodeToolType =
  | 'copy'
  | 'image'
  | 'video'
  | 'video_omni'
  | 'video_seedance'
  | 'compose'
  | 'video_merge'
  | 'video_reel'
  | 'video_short_a'
  | 'video_short_b';

export const CREATIVE_NODE_KINDS: ReadonlyArray<CreativeNodeKind> = ['ROOT', 'TOOL', 'OUTPUT'];
export const CREATIVE_NODE_TOOL_TYPES: ReadonlyArray<CreativeNodeToolType> = [
  'copy',
  'image',
  'video',
  'video_omni',
  'video_seedance',
  'compose',
  // @backend-pending(Q2): 見 CreativeNodeToolType 上方註解。
  'video_merge',
  'video_reel',
  'video_short_a',
  'video_short_b',
];

/**
 * Server-owned field — patch body 含這些一律由 client 端 `sanitizePatchForOcc` 剝除。
 * 前 7 個是 spec §3.6 immutable field(含這些 PATCH 會 422 GRAPH_OPS_INVALID);
 * `locked`(ADR-026)是 green TOOL 產過 asset 後的 server-owned 唯讀旗標,FE 只讀不送
 * (對 locked node 改 config/tool_type/asset_id 會 422 NODE_LOCKED — 物理剝除當雙保險)。
 */
export const CREATIVE_NODE_IMMUTABLE_FIELDS: ReadonlyArray<string> = [
  'nid',
  'node_kind',
  'schema_version',
  'version',
  'created_at',
  'created_by',
  'updated_by',
  'locked',
  // docs/76 §5.1 — 綠卡產物歷史(server-owned,advance tx 內 append;FE 只讀)。
  'produced_history',
  // R408.2(BE docs/134 / ADR-053)— 使用者刪過承接藍卡的產物(server-owned;PATCH 帶 = 422)。
  'dismissed_item_ids',
];

// ─── DTOs (snake_case wire format) ───────────────────────────────────────────

/**
 * docs/76 §5.1 — 綠卡產物歷史單筆(server-owned;最多 24 筆、最新在末筆,超限 trim 最舊)。
 * `item_id` 是穩定唯一 id = FE 建藍卡的冪等鍵(bind_from);**新筆偵測用 item_id 集合差**,
 * 禁用陣列長度/索引(trim 會縮短)與 version 欄(server 寫歷史不 bump version)。
 */
export interface ProducedHistoryItemDto {
  item_id: string;
  asset_id: string;
  asset_kind: 'TEXT' | 'IMAGE' | 'VIDEO' | 'COMPOSE';
}

export interface CreativeNodeDto {
  schema_version: 'v0.0.1';
  nid: string;
  node_kind: CreativeNodeKind;
  tool_type?: CreativeNodeToolType | null;
  /**
   * **Typed executor input**(`NodeConfig` discriminated union)— `extra="forbid"`,
   * 只接 typed key per tool_type(BE commit 87959b4):
   *   - COPY: `tone` / `locale` / `word_count_hint`
   *   - IMAGE: `aspect_ratio` / `style_keywords`
   *   - VIDEO: `duration_seconds` / `aspect_ratio` / `seed`
   *   - COMPOSE: `output_format` / `aspect_ratios`
   *
   * **任何其他 key 都會 422 撞 worker** — UI text 走 `display_label` / `display_meta`,
   * 不要塞 `config` 內。
   */
  config: Record<string, unknown>;
  position: { x: number; y: number };
  text_value?: string | null;
  asset_id?: string | null;
  /**
   * ADR-024 §5 — OUTPUT result-holder 的 server-owned 投影:綠 step 成功後 worker
   * 自動把產出 asset 寫進「下游那張 OUTPUT」的此欄(`mirror_write_output_node_assets`,
   * version-free / idempotent)。**T3 fan-out 的藍卡靠這個顯圖**(非 `asset_id`)。
   * FE 只讀不寫。
   */
  projected_asset_ids?: string[] | null;
  /**
   * R135(docs/75 v2.1 §11)— 橘卡 multi-slot(影片 ≤1 / 圖 ≤3 / 文字 ≤10)。
   * ROOT 節點回應含全量;PATCH 為**整包覆寫**(帶編輯後完整 slots,非差分)。
   * legacy `asset_id`/`text_value` 照舊有效(BE 對 media 取聯集去重)。
   */
  slots?: {
    video_asset_ids?: string[];
    image_asset_ids?: string[];
    text_values?: string[];
    /** docs/77 §2(BE-1)— 角色分格:產品圖 ≤1 / AI 模特兒參考圖 ≤1 / 其它 ≤1。
        圖片格合計(product+character+extra+legacy image)≤3(BE 硬界)。 */
    product_asset_ids?: string[];
    character_asset_ids?: string[];
    /** R190(BE `dev-update-v11-32`)— 場景格:格網標注 `Scene 1..4`,與 extra 語意分家。 */
    scene_asset_ids?: string[];
    extra_asset_ids?: string[];
    /** R404 — Logo 格(**FE 提案欄名,BE 未開欄**;docs/54 §R404)。舊 BE 不回 → normalize `[]`。 */
    logo_asset_ids?: string[];
    /** docs/81 §3(R180)— 商品資訊(非 asset,不佔格);submit 時 BE 自動摺進需求描述。 */
    product_info?: ProductInfoDto | null;
    /** docs/97 §5.1(R274.1;FB-20260820-0011)— 模特兒名稱。⚠️ **不摺進 prompt**(owner 拍板)。 */
    character_info?: SlotSubjectInfoDto | null;
    /** docs/97 §5.1 — 場景名稱(與 character_info 同構;純識別標籤)。 */
    scene_info?: SlotSubjectInfoDto | null;
    /** BE docs/131(ADR-051;R395)— per-asset 名稱(asset_id → 名稱;@ chip 顯示名)。 */
    asset_aliases?: Record<string, string> | null;
  } | null;
  /**
   * docs/76 §5.1(ADR-035 雙色卡)— 綠卡產物歷史(server-owned;FE 只讀,
   * `CREATIVE_NODE_IMMUTABLE_FIELDS` 物理剝除)。FE 依此建/綁真藍卡(producedHistoryPlan)。
   */
  produced_history?: ProducedHistoryItemDto[] | null;
  /**
   * R408.2(BE docs/134 / ADR-053)— 使用者已刪掉其承接藍卡的產物 `item_id`(TOOL-only;
   * server-owned、**version-free**:BE 寫入不 bump `version`)。FE planner 跳過這些筆,刪掉的
   * 藍卡才不會被 `bind_from` 建回來。GET graph 一律有;onSnapshot 原始 doc **沒被刪過卡就沒有
   * 這個 key** → 當 `[]`。
   */
  dismissed_item_ids?: string[] | null;
  /** R50.6 — FE UI node title(executor 不讀;BE 2026-05-26 commit 87959b4 加,max 120)。 */
  display_label?: string | null;
  /** R50.6 — FE UI node subtitle(executor 不讀;max 200)。 */
  display_meta?: string | null;
  /**
   * ADR-026 — green TOOL 成功產過 asset 後 = `true`。**server-owned 唯讀**:
   * FE 只讀(顯鎖頭 + 設定面板唯讀),**絕不**放進 PATCH body
   * (`CREATIVE_NODE_IMMUTABLE_FIELDS` 雙保險剝除)。對 locked node 送
   * config/tool_type/asset_id 會 422 `NODE_LOCKED`。
   */
  locked?: boolean;
  version: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by: string;
}

export interface CreateCreativeNodeRequestDto {
  node_kind: CreativeNodeKind;
  tool_type?: CreativeNodeToolType;
  /** Typed executor input — 嚴格 white-list per tool_type;見 CreativeNodeDto.config JSDoc。 */
  config?: Record<string, unknown>;
  position?: { x: number; y: number };
  text_value?: string;
  asset_id?: string;
  /**
   * docs/76 §2.1(ADR-035)+ docs/77 §2(BE-1 角色格)— 建綠卡帶素材區(圖合計 ≤3 /
   * 影 ≤1;寫入當下逐 id 驗,上傳 id 自動 promotion)。text_values 是橘卡存量欄,不送。
   */
  slots?: {
    image_asset_ids?: string[];
    video_asset_ids?: string[];
    product_asset_ids?: string[];
    character_asset_ids?: string[];
    scene_asset_ids?: string[];
    extra_asset_ids?: string[];
    /**
     * R404 — Logo 格(FE 提案欄名;docs/54 §R404)。⚠️ **只在非空時才放進 body**:BE 未開欄前
     * `NodeSlots` 是 `extra="forbid"`,帶這個鍵 = 整包 422。
     */
    logo_asset_ids?: string[];
    /** docs/81 §3 — 商品資訊(duplicateConcept 等建卡路徑一併帶,不然複製會掉)。 */
    product_info?: ProductInfoDto | null;
    /** docs/97 §5.1 — 模特兒 / 場景名稱(P-77.4 同則:整包覆寫的建卡路徑漏帶 = 複製掉名字)。 */
    character_info?: SlotSubjectInfoDto | null;
    scene_info?: SlotSubjectInfoDto | null;
    /** docs/131 — per-asset 名稱(複製 / 建卡路徑漏帶 = 名字掉光,同 info 三欄)。 */
    asset_aliases?: Record<string, string>;
  };
  /**
   * docs/76 §2.3A(ADR-035)— 承接產物建藍卡:server 一次完成鑄 nid + 綁 asset +
   * 建「綠→藍」邊,回 `{node, edge}`(走 `dagService.nodes.createBound`)。
   * 同一 `item_id` 冪等(重複呼叫回同一張既有卡)。
   */
  bind_from?: { source_node_id: string; item_id: string };
  /** R50.6 — FE UI node title(BE 2026-05-26 加)。 */
  display_label?: string;
  /** R50.6 — FE UI node subtitle。 */
  display_meta?: string;
}

/** docs/76 §2.3A — bind_from 建卡回應(node + server 自建的綠→藍 edge)。 */
export interface CreateBoundOutputResponseDto {
  node: CreativeNodeDto;
  edge: NodeEdgeDto;
}

export interface UpdateCreativeNodeRequestDto {
  patch: Record<string, unknown>;
  expected_version: number;
}

export interface CreativeNodeResponseDto {
  node: CreativeNodeDto;
}

export interface NodeEdgeDto {
  schema_version: 'v0.0.1';
  eid: string;
  source_node_id: string;
  target_node_id: string;
  created_at: string;
  created_by: string;
}

export interface CreateNodeEdgeRequestDto {
  source_node_id: string;
  target_node_id: string;
}

export interface NodeEdgeResponseDto {
  edge: NodeEdgeDto;
}

export interface GraphAggregateResponseDto {
  schema_version: 'v0.0.1';
  nodes: CreativeNodeDto[];
  edges: NodeEdgeDto[];
}

/**
 * docs/81 §3(R180)— 綠卡商品資訊 wire 形狀(`slots.product_info`)。
 * 皆選填;字串 ≤120/≤120/≤50、尺寸 cm >0 ≤100000(0/缺 = 未填,負數/超界 422
 * `SLOT_LIMIT_EXCEEDED`);全空 BE normalize 成 null。**非 asset**:不佔格、不計價。
 * submit 時 BE 自動摺進需求描述(`text_value` 不會被改寫,FE 也不要自己拼進去)。
 * ⚠️ slots 是 LWW 整包覆寫 — **每次 PATCH slots 都要帶著 product_info,漏帶 = 清空**。
 */
export interface ProductInfoDto {
  name?: string | null;
  brand?: string | null;
  /** 自由字串 ≤50;UI 四選一直接送中文標籤(一般/服飾/褲子/鞋類,docs/81 §3)。 */
  product_type?: string | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
}

/** `ProductInfoDto` 的 camel 版(FE domain;欄位全正規化為 `x | null`)。 */
export interface ProductInfo {
  name: string | null;
  brand: string | null;
  productType: string | null;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
}

/** camel → wire(PATCH/create slots 用);null 透傳(BE 對 null normalize)。 */
export function productInfoToDto(pi: ProductInfo | null | undefined): ProductInfoDto | null {
  if (!pi) return null;
  return {
    name: pi.name,
    brand: pi.brand,
    product_type: pi.productType,
    length_cm: pi.lengthCm,
    width_cm: pi.widthCm,
    height_cm: pi.heightCm,
  };
}

/** wire → camel;全空(所有欄位缺/空)normalize 成 null(對齊 BE 行為)。 */
function mapProductInfoDto(dto: ProductInfoDto | null | undefined): ProductInfo | null {
  if (!dto) return null;
  const pi: ProductInfo = {
    name: dto.name ?? null,
    brand: dto.brand ?? null,
    productType: dto.product_type ?? null,
    lengthCm: dto.length_cm ?? null,
    widthCm: dto.width_cm ?? null,
    heightCm: dto.height_cm ?? null,
  };
  const hasAny =
    !!pi.name || !!pi.brand || !!pi.productType || pi.lengthCm !== null || pi.widthCm !== null || pi.heightCm !== null;
  return hasAny ? pi : null;
}

/**
 * docs/97 §5.1(R274.1;FB-20260820-0011「模特兒名稱不會儲存」)— 模特兒 / 場景
 * 「主體資訊」wire 形狀(`slots.character_info` / `slots.scene_info`)。目前只有名稱。
 *
 * ⚠️ 與 `product_info` 的差別:**名稱不會摺進生成提示詞**(BE 刻意 — 人名進 brief 會把
 * 模型帶去畫同名名人,與「照著這張照片的人」衝突;要改是 owner 決策)。純識別標籤。
 * ⚠️ slots 是 LWW 整包覆寫 — 每次 PATCH slots 都要帶著,漏帶 = 清空(同 product_info)。
 * BE 對空物件 / 空白 name 一律 normalize 成 null(FE mapper 鏡射同語意)。
 */
export interface SlotSubjectInfoDto {
  name?: string | null;
}

/** `SlotSubjectInfoDto` 的 camel 版(FE domain)。 */
export interface SlotSubjectInfo {
  name: string | null;
}

/** camel → wire;全空(name 缺/空白)送 null(BE 也會 normalize,兩邊同語意少一種中間態)。 */
export function slotSubjectInfoToDto(si: SlotSubjectInfo | null | undefined): SlotSubjectInfoDto | null {
  const name = (si?.name ?? '').trim();
  return name ? { name } : null;
}

/** wire → camel;空物件 / 空白 name normalize 成 null。 */
function mapSlotSubjectInfoDto(dto: SlotSubjectInfoDto | null | undefined): SlotSubjectInfo | null {
  const name = (dto?.name ?? '').trim();
  return name ? { name } : null;
}

// ─── camelCase domain types ──────────────────────────────────────────────────

/** docs/76 §5.1 — 產物歷史單筆(camel;見 `ProducedHistoryItemDto`)。 */
export interface ProducedHistoryItem {
  itemId: string;
  assetId: string;
  assetKind: 'TEXT' | 'IMAGE' | 'VIDEO' | 'COMPOSE';
}

export interface CreativeNode {
  nid: string;
  nodeKind: CreativeNodeKind;
  toolType: CreativeNodeToolType | null;
  config: Record<string, unknown>;
  position: { x: number; y: number };
  textValue: string | null;
  assetId: string | null;
  /**
   * ADR-024 §5 — OUTPUT result-holder server 投影(綠生成後 worker 自動填)。T3 藍卡
   * 靠此顯圖(非 `assetId`)。缺值 normalize 成 `[]`。FE 只讀不寫。
   */
  projectedAssetIds: string[];
  /**
   * R135(docs/75 §11)— 橘卡 multi-slot(camel;缺值 normalize 成三空陣列)。
   * 目前 FE 為**讀取側**(A/B 掃圖 + 橘卡唯讀顯示);整包編輯 UI 待拍板
   * (與 R60/R67「橘卡編輯區唯讀、素材在來源頁定稿」既有拍板相抵)。
   */
  slots: {
    videoAssetIds: string[];
    imageAssetIds: string[];
    textValues: string[];
    /** docs/77 §2 角色分格(camel;缺值 normalize `[]`):產品 ≤1 / 模特兒參考 ≤1 / 其它 ≤1。 */
    productAssetIds: string[];
    characterAssetIds: string[];
    sceneAssetIds: string[];
    extraAssetIds: string[];
    /** R404 — Logo 格(缺值 normalize `[]`;BE 開欄前恆空)。 */
    logoAssetIds: string[];
    /** docs/81 §3(R180)— 商品資訊(缺/全空 normalize `null`;LWW — PATCH slots 必帶)。 */
    productInfo: ProductInfo | null;
    /** docs/97 §5.1(R274.1)— 模特兒名稱(純識別;不進 prompt;LWW 必帶)。 */
    characterInfo: SlotSubjectInfo | null;
    /** docs/97 §5.1 — 場景名稱(同上)。 */
    sceneInfo: SlotSubjectInfo | null;
    /** docs/131(R395)— per-asset 名稱(缺值 normalize `{}`;LWW — PATCH slots 必帶)。 */
    assetAliases: Record<string, string>;
  };
  /**
   * docs/76 §5.1(ADR-035)— 綠卡產物歷史(server-owned 只讀;缺值 normalize `[]`)。
   * 新筆偵測 = item_id 集合差(禁長度/索引/version);FE 依此建/綁真藍卡。
   */
  producedHistory: ProducedHistoryItem[];
  /** R408.2(docs/134)— 使用者刪過承接藍卡的產物 item_id(server-owned 只讀;缺值 normalize `[]`)。 */
  dismissedItemIds: string[];
  /** R50.6 — FE UI node title(BE 2026-05-26 commit 87959b4 加)。 */
  displayLabel: string | null;
  /** R50.6 — FE UI node subtitle。 */
  displayMeta: string | null;
  /**
   * ADR-026 — green TOOL 產過 asset 後 = `true`(server-owned 唯讀)。缺值 normalize 成
   * `false`(對齊 mapper 邊界防漂值契約)。FE 只讀:locked 控件唯讀 + 顯鎖頭 + 提供複製節點。
   */
  locked: boolean;
  /** OCC SoT — 每次 PATCH 必帶 `expected_version`。BE 1-based 自增。 */
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string;
}

export interface NodeEdge {
  eid: string;
  sourceNodeId: string;
  targetNodeId: string;
  createdAt: string;
  createdBy: string;
}

export interface GraphAggregate {
  nodes: CreativeNode[];
  edges: NodeEdge[];
}

// ─── R134 — 節點即時驗證(docs/75 §3;A/B 卡缺件/blocked 提示)────────────────

export interface NodeValidationReasonDto {
  code: string;
  message: string;
  severity: 'BLOCKED' | 'WARNING';
  /**
   * docs/76 §8/§9 — 部分 reason 帶結構化細節(如 `DEPENDENCY_UNSATISFIED` 的
   * `required_upstream_node_id`);shape per-code,消費端型別守衛後用。
   */
  details?: Record<string, unknown>;
}

export interface NodeValidationResponseDto {
  state: 'OK' | 'WARNING' | 'BLOCKED';
  reasons?: NodeValidationReasonDto[];
  missing_fields?: string[];
}

export interface NodeValidationResult {
  state: 'OK' | 'WARNING' | 'BLOCKED';
  reasons: NodeValidationReasonDto[];
  missingFields: string[];
}

export function mapNodeValidationResponseDto(dto: NodeValidationResponseDto): NodeValidationResult {
  return {
    state: dto.state,
    reasons: dto.reasons ?? [],
    missingFields: dto.missing_fields ?? [],
  };
}

// ─── R208 — 範本示範素材伺服端匯入(docs/85 §6.2 / docs/86 / ADR-042)─────────────

/** `include` 值域(封閉;缺 = 三類全上)。 */
/**
 * R350.7(docs/85 §3.2)— `extra` = 「其他/額外參考」格(`NodeSlots.extra_asset_ids`);
 * **omni 影片範本的九宮格分鏡走這一格**。BE `INCLUDE_CLASSES` 早就是四值,FE 少送一個
 * 就是「分鏡靜默不會載入」(BE 只在 include 沒帶時才預設全帶,而 FE 一律顯式送)。
 */
export type TemplateDemoImportRole = 'product' | 'scene' | 'character' | 'extra';

export interface ImportTemplateDemoRequestDto {
  template_id: string;
  include?: TemplateDemoImportRole[];
}

/**
 * 匯入 per-file skip 明細。`reason` 是封閉 enum(`SOURCE_NOT_FOUND` / `SOURCE_NOT_ALLOWED`
 * / `UNSUPPORTED_KIND` / `CHARACTER_NOT_FOUND` / `URI_MISMATCH`)— FE 只做計數與 log,
 * 不逐值分流(未知值不炸)。⚠️ `CHARACTER_NOT_FOUND` 那筆的 `url` 是 `character:<id>`
 * 參照字串**不是 URL** — 一律當 opaque 字串,不進任何 URL parser。
 */
export interface TemplateDemoSkippedDto {
  url: string;
  reason: string;
}

export interface ImportTemplateDemoResponseDto {
  product?: string[] | null;
  scene?: string[] | null;
  character?: string[] | null;
  extra?: string[] | null;
  character_id?: string | null;
  library_id?: string | null;
  skipped?: TemplateDemoSkippedDto[] | null;
  copied_count?: number;
  reused_count?: number;
  refreshed_count?: number;
}

/**
 * 匯入結果(domain shape)。`product` / `scene` / `character` = **後端已綁進
 * node.slots 的 asset id**(replace 語意,只動有素材落地的角色格)— FE 用它更新
 * 本地 store,**不再自己 PATCH 這些格**;`characterId` 非 null = 後端已寫卡
 * `config.character_id`(FE 只鏡射 UI 選中態)。
 */
export interface TemplateDemoImportResult {
  product: string[];
  scene: string[];
  character: string[];
  extra: string[];
  characterId: string | null;
  libraryId: string | null;
  skipped: TemplateDemoSkippedDto[];
  copiedCount: number;
  reusedCount: number;
  refreshedCount: number;
}

export function mapImportTemplateDemoResponseDto(
  dto: ImportTemplateDemoResponseDto,
): TemplateDemoImportResult {
  return {
    product: dto.product ?? [],
    scene: dto.scene ?? [],
    character: dto.character ?? [],
    extra: dto.extra ?? [],
    characterId: dto.character_id ?? null,
    libraryId: dto.library_id ?? null,
    skipped: dto.skipped ?? [],
    copiedCount: dto.copied_count ?? 0,
    reusedCount: dto.reused_count ?? 0,
    refreshedCount: dto.refreshed_count ?? 0,
  };
}

// ─── business-envelope unwrap(`{node}` / `{edge}`)──────────────────────────

/**
 * 解 **node 業務 envelope**:`{ node: CreativeNodeDto }`(spec §3.6 — node CRUD
 * endpoint 包這層)。部分 endpoint 可能直回裸 `CreativeNodeDto`(舊式 vs 新式),
 * helper 兼容兩種 shape。
 *
 * **注意層級**:這是 per-resource 業務 envelope,跟 R81 BaseResponse 外殼
 * `{ result }`(`httpEnvelope.unwrapResult`)是**不同層** —— wire 真實順序是
 * `{ result: { node: ... } }`,先 `unwrapResult` 剝外殼、再 `unwrapNodeEnvelope` 剝內層。
 *
 * `{ node: null }` 這種 falsy envelope 視為「沒包」→ 走 backward-compat 直回原物件
 * (`&& x.node` 真值守衛;行為對齊原 httpDagService.unwrapNode / canvasAi.unwrapNodeDto)。
 */
export function unwrapNodeEnvelope(
  x: CreativeNodeResponseDto | CreativeNodeDto,
): CreativeNodeDto {
  return 'node' in x && x.node ? x.node : (x as CreativeNodeDto);
}

/**
 * 解 **edge 業務 envelope**:`{ edge: NodeEdgeDto }`。語意同 `unwrapNodeEnvelope`
 * (兼容裸 DTO / falsy `{ edge: null }` 走 backward-compat)。
 */
export function unwrapEdgeEnvelope(
  x: NodeEdgeResponseDto | NodeEdgeDto,
): NodeEdgeDto {
  return 'edge' in x && x.edge ? x.edge : (x as NodeEdgeDto);
}

// ─── mappers ─────────────────────────────────────────────────────────────────

/**
 * 座標防呆(R270)。
 *
 * 原本是 `dto.position ?? {x:0,y:0}` —— 只擋 `null`/缺席。但 BE 的
 * `CreativeNode.position` 是 `Field(default_factory=dict)`,ADR-044 的
 * `copy_gen/blue_card.py` 補建藍卡時**沒有帶 position** → wire 上是 `{}`,
 * 對 `??` 而言是 truthy → `{x: undefined, y: undefined}` 直接進 reactflow,
 * transform 變 `NaN` → 那張卡不知道畫到哪去(而且 NaN 會沿 tidy / findFreePosition
 * 擴散到別張卡)。
 *
 * 逐軸檢查有限數值,不合法就補 0:座標是**顯示層**資料,寧可疊在原點讓人看得到、
 * 拖得動,也不要 NaN。⚠️ 這不是幫 BE 補完 —— 補建卡沒有位置本身要 BE 修
 * (docs/54 §G.20),這裡只保證 FE 不會因此壞掉。
 */
function safePosition(raw: { x?: number; y?: number } | null | undefined): {
  x: number;
  y: number;
} {
  const x = raw?.x;
  const y = raw?.y;
  return {
    x: typeof x === 'number' && Number.isFinite(x) ? x : 0,
    y: typeof y === 'number' && Number.isFinite(y) ? y : 0,
  };
}

export function mapCreativeNodeDto(dto: CreativeNodeDto): CreativeNode {
  return {
    nid: dto.nid,
    nodeKind: dto.node_kind,
    toolType: dto.tool_type ?? null,
    config: dto.config ?? {},
    position: safePosition(dto.position),
    textValue: dto.text_value ?? null,
    assetId: dto.asset_id ?? null,
    projectedAssetIds: dto.projected_asset_ids ?? [],
    slots: {
      videoAssetIds: dto.slots?.video_asset_ids ?? [],
      imageAssetIds: dto.slots?.image_asset_ids ?? [],
      textValues: dto.slots?.text_values ?? [],
      productAssetIds: dto.slots?.product_asset_ids ?? [],
      characterAssetIds: dto.slots?.character_asset_ids ?? [],
      sceneAssetIds: dto.slots?.scene_asset_ids ?? [],
      extraAssetIds: dto.slots?.extra_asset_ids ?? [],
      logoAssetIds: dto.slots?.logo_asset_ids ?? [],
      productInfo: mapProductInfoDto(dto.slots?.product_info),
      characterInfo: mapSlotSubjectInfoDto(dto.slots?.character_info),
      sceneInfo: mapSlotSubjectInfoDto(dto.slots?.scene_info),
      assetAliases: dto.slots?.asset_aliases ?? {},
    },
    producedHistory: (dto.produced_history ?? []).map((it) => ({
      itemId: it.item_id,
      assetId: it.asset_id,
      assetKind: it.asset_kind,
    })),
    dismissedItemIds: dto.dismissed_item_ids ?? [],
    displayLabel: dto.display_label ?? null,
    displayMeta: dto.display_meta ?? null,
    locked: dto.locked ?? false,
    version: dto.version,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    createdBy: dto.created_by ?? null,
    updatedBy: dto.updated_by,
  };
}

export function mapNodeEdgeDto(dto: NodeEdgeDto): NodeEdge {
  return {
    eid: dto.eid,
    sourceNodeId: dto.source_node_id,
    targetNodeId: dto.target_node_id,
    createdAt: dto.created_at,
    createdBy: dto.created_by,
  };
}

export function mapGraphAggregateResponseDto(dto: GraphAggregateResponseDto): GraphAggregate {
  // Round 50 defensive `?? []` — BE 在 transient state(graph 剛建立 / Firestore listener
  // 提早 fire)或漂值情境可能回 `nodes: null` / `edges: null`,FE 端 `.map(null)` 直接 crash
  // 整個 canvas。Mapper 邊界 normalize 確保下游 store / reactflow 永遠拿到陣列(對齊
  // R41.0 mapper-edge normalize 契約 + ADR-002 SSOT「FE 拿到的 shape 始終完整」)。
  return {
    nodes: (dto.nodes ?? []).map(mapCreativeNodeDto),
    edges: (dto.edges ?? []).map(mapNodeEdgeDto),
  };
}

/**
 * Client-side 白名單 sanitize:`patch` body 移除 immutable field
 * (完整清單以 `CREATIVE_NODE_IMMUTABLE_FIELDS` array 為準)。
 * 對應 spec §3.6 — 違規會被 BE 422 `GRAPH_OPS_INVALID`,FE 預先 drop 比較友善。
 *
 * @returns sanitized patch object(新物件,不 mutate input)
 */
export function sanitizePatchForOcc(
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(patch)) {
    if (CREATIVE_NODE_IMMUTABLE_FIELDS.includes(k)) continue;
    out[k] = v;
  }
  return out;
}
